import * as Dialog from "@radix-ui/react-dialog";
import { ChevronDown, ChevronUp, Plus, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { useToast } from "@/hooks/use-toast";
import { listUsers } from "@/api/admin-api";
import type { UserPublic } from "@/api/auth-api";
import { useQuery } from "@tanstack/react-query";
import {
  fetchLastSelection,
  useAllCases,
  useCreateDefinition,
  useDefinitionCases,
  usePatchDefinition,
  useSetDefinitionCases,
  useTestSettings,
} from "@/api/tests-api";
import { formatCountdown } from "@/hooks/useTestCountdown";
import { ExamListPanel } from "./ExamListPanel";

/**
 * Build a test, then hand it to a candidate.
 *
 * The question selection is pre-ticked from whatever this admin chose on
 * their previous test. That is not stored anywhere as a preference — the
 * previous definition already records it, so the backend just reads it
 * back.
 */
export function TestBuilderPanel() {
  const { toast } = useToast();
  const settings = useTestSettings();
  // Only the hidden QuestionOrderPicker consumed this. Kept commented
  // rather than deleted so restoring the picker is one un-comment.
  // const questions = useAllQuestions();
  const cases = useAllCases();
  const createDefinition = useCreateDefinition();
  const patchDefinition = usePatchDefinition();
  const setCases = useSetDefinitionCases();

  const candidates = useQuery({
    queryKey: ["admin", "users", "testrad"],
    queryFn: () => listUsers(),
    select: (rows: UserPublic[]) =>
      rows.filter((u) => u.role === "TESTRAD" && u.active),
  });

  const lastSelection = useQuery({
    queryKey: ["tests", "last-selection"],
    queryFn: fetchLastSelection,
  });

  const [title, setTitle] = useState("");
  const [duration, setDuration] = useState("");
  // Blank means this exam offers no practice run at all — which is a
  // different thing from a practice run of zero length.
  const [dryRunDuration, setDryRunDuration] = useState("");
  const [introPrompt, setIntroPrompt] = useState("");
  const [pickedQuestions, setPickedQuestions] = useState<string[]>([]);
  // Cases are picked per PHASE: what a candidate rehearses on is never
  // what they are judged on.
  const [liveCases, setLiveCases] = useState<string[]>([]);
  const [practiceCases, setPracticeCases] = useState<string[]>([]);
  // Non-null while an existing exam is being edited rather than a new
  // one built. Editing is possible at all because candidates hold their
  // own sealed copy — see ExamListPanel.
  const [editingId, setEditingId] = useState<string | null>(null);
  // Building a NEW exam. Separate from editingId because "no id yet" and
  // "the dialog is shut" are different things.
  const [creating, setCreating] = useState(false);

  const editingCases = useDefinitionCases(editingId);

  // Pre-tick the previous selection, once, when it arrives.
  const [seeded, setSeeded] = useState(false);
  useEffect(() => {
    if (seeded || !lastSelection.data) return;
    setSeeded(true);
    if (lastSelection.data.length) setPickedQuestions(lastSelection.data);
  }, [lastSelection.data, seeded]);

  const defaultDuration = settings.data?.live_default_duration_seconds ?? 0;
  const defaultDryRun = settings.data?.dry_run_duration_seconds ?? 0;

  // Fill both boxes once the defaults arrive, rather than leaving them
  // blank and letting the server pick. The length of an assessment
  // should be a number the person setting it can SEE — a hidden fallback
  // is how a practice run quietly ends up two hours long.
  const prefilledRef = useRef(true);
  useEffect(() => {
    if (!defaultDuration || !prefilledRef.current) return;
    setDuration(String(Math.round(defaultDuration / 60)));
    setDryRunDuration(String(Math.round(defaultDryRun / 60)));
  }, [defaultDuration, defaultDryRun]);

  // When Edit is pressed the exam's own cases arrive asynchronously;
  // seed the pickers once they do, and only once, so a later refetch
  // cannot discard a change made in the meantime.
  const seededCasesFor = useRef<string | null>(null);
  useEffect(() => {
    if (!editingId || !editingCases.data) return;
    if (seededCasesFor.current === editingId) return;
    seededCasesFor.current = editingId;
    setLiveCases(
      editingCases.data.filter((c) => c.phase === "LIVE").map((c) => c.test_case_id),
    );
    setPracticeCases(
      editingCases.data
        .filter((c) => c.phase === "DRY_RUN")
        .map((c) => c.test_case_id),
    );
  }, [editingId, editingCases.data]);

  const beginEdit = (exam: {
    id: string;
    title: string;
    duration_seconds: number;
    dry_run_duration_seconds: number | null;
    intro_prompt: string | null;
    selected_question_ids: string[];
  }) => {
    prefilledRef.current = false;
    seededCasesFor.current = null;
    setCreating(false);
    setEditingId(exam.id);
    setTitle(exam.title);
    setDuration(String(Math.round(exam.duration_seconds / 60)));
    setDryRunDuration(
      exam.dry_run_duration_seconds === null
        ? ""
        : String(Math.round(exam.dry_run_duration_seconds / 60)),
    );
    setIntroPrompt(exam.intro_prompt ?? "");
    setPickedQuestions(exam.selected_question_ids ?? []);
    setLiveCases([]);
    setPracticeCases([]);
  };

  const resetForm = () => {
    setEditingId(null);
    setCreating(false);
    seededCasesFor.current = null;
    setTitle("");
    setIntroPrompt("");
    setLiveCases([]);
    setPracticeCases([]);
    prefilledRef.current = true;
    setDuration(String(Math.round(defaultDuration / 60)));
    setDryRunDuration(String(Math.round(defaultDryRun / 60)));
  };

  const minutesOf = (v: string) => Number(v.trim());
  const validMinutes = (v: string) => {
    const n = minutesOf(v);
    return v.trim().length > 0 && Number.isFinite(n) && n >= 1 && n <= 1440;
  };

  const durationValid = validMinutes(duration);
  // Blank is allowed here: it means "no practice run".
  const dryRunValid = dryRunDuration.trim().length === 0 || validMinutes(dryRunDuration);
  const offersDryRun = dryRunDuration.trim().length > 0 && dryRunValid;

  const canCreate =
    title.trim().length > 0 &&
    durationValid &&
    dryRunValid &&
    !createDefinition.isPending &&
    !patchDefinition.isPending;

  const create = async () => {
    if (!canCreate) return;
    const body = {
      title: title.trim(),
      duration_seconds: Math.round(minutesOf(duration) * 60),
      dry_run_duration_seconds: offersDryRun
        ? Math.round(minutesOf(dryRunDuration) * 60)
        : null,
      intro_prompt: introPrompt.trim() || null,
      selected_question_ids: pickedQuestions,
    };
    // The case list is written WHOLE, including when it is now empty —
    // clearing every case is a legitimate edit, and skipping the call on
    // an empty list would silently keep the old ones.
    const cases = [
      ...practiceCases.map((id) => ({
        test_case_id: id,
        phase: "DRY_RUN" as const,
      })),
      ...liveCases.map((id) => ({ test_case_id: id, phase: "LIVE" as const })),
    ];
    try {
      if (editingId) {
        await patchDefinition.mutateAsync({ definitionId: editingId, body });
        await setCases.mutateAsync({ definitionId: editingId, cases });
        resetForm();
        toast({ title: "Exam saved" });
        return;
      }
      const def = await createDefinition.mutateAsync(body);
      if (cases.length) {
        await setCases.mutateAsync({ definitionId: def.id, cases });
      }
      resetForm();
      toast({ title: "Exam created" });
    } catch (err) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ??
        (editingId ? "Could not save the exam" : "Could not create the exam");
      toast({ title: detail, variant: "error" });
    }
  };

  // Only cases with images can be sat, and the server refuses the rest.
  // Offering them anyway means picking something, pressing Save, and
  // being told no — so they are not offered. The count of what was left
  // out is shown, because a silently shorter list looks like data loss.
  const allCases = cases.data ?? [];
  const sittableCases = allCases.filter((c) => c.instance_count > 0);
  const emptyCaseCount = allCases.length - sittableCases.length;

  const toggle = (list: string[], id: string) =>
    list.includes(id) ? list.filter((x) => x !== id) : [...list, id];

  const examForm = (
    <>
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <label className="flex flex-col text-xs text-foreground-muted sm:col-span-2">
            Title
            <input
              data-cy="definition-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="sk-input mt-1 w-auto"
            />
          </label>
          <label className="sk-field flex flex-col">
            <span className="sk-label">Practice run (minutes)</span>
            <input
              data-cy="definition-dry-run-duration"
              value={dryRunDuration}
              onChange={(e) => {
                prefilledRef.current = false;
                setDryRunDuration(e.target.value);
              }}
              inputMode="numeric"
              placeholder="none"
              aria-invalid={!dryRunValid}
              className={[
                "mt-1 rounded-md border bg-input-bg px-3 py-2 text-sm text-foreground",
                dryRunValid ? "border-input-border" : "border-status-urgent",
              ].join(" ")}
            />
            <span className="mt-1 text-[11px]">
              {!dryRunValid
                ? "Between 1 and 1440 minutes, or blank."
                : offersDryRun
                  ? "Practice time is never counted against the candidate."
                  : "Leave blank for no practice run."}
            </span>
          </label>
          <label className="sk-field flex flex-col">
            <span className="sk-label">Assessment (minutes)</span>
            <input
              data-cy="definition-duration"
              value={duration}
              onChange={(e) => {
                prefilledRef.current = false;
                setDuration(e.target.value);
              }}
              inputMode="numeric"
              aria-invalid={duration.trim().length > 0 && !durationValid}
              className={[
                "mt-1 rounded-md border bg-input-bg px-3 py-2 text-sm text-foreground",
                duration.trim().length > 0 && !durationValid
                  ? "border-status-urgent"
                  : "border-input-border",
              ].join(" ")}
            />
            <span className="mt-1 text-[11px]">
              {durationValid
                ? `The candidate gets ${formatCountdown(Math.round(minutesOf(duration) * 60))}.`
                : "Between 1 and 1440 minutes."}
            </span>
          </label>
          <label className="flex flex-col text-xs text-foreground-muted sm:col-span-2">
            {/* Renders UNDER the application's own lede, not instead of
                it -- an admin typing here adds to the standard form
                rather than replacing its explanation. */}
            Note shown on the application, before the cases
            <textarea
              data-cy="definition-intro"
              rows={2}
              value={introPrompt}
              onChange={(e) => setIntroPrompt(e.target.value)}
              className="sk-input mt-1 w-auto"
            />
          </label>
        </div>

        {/* THE INTRODUCTION-QUESTION PICKER IS HIDDEN, not deleted.
            Step one of a sitting is the fixed nine-section candidate
            application (CandidateApplicationForm), which every candidate
            answers regardless of what is selected here -- so the picker
            showed "Introduction questions (0)" on every exam and implied
            candidates were asked nothing. That reads as a missing
            feature and is the opposite of the truth.

            `pickedQuestions` and `selected_question_ids` are still
            wired: the state, the payload and the seal are untouched, so
            an exam created now keeps whatever it already had rather than
            silently having its selection cleared. Restoring the picker
            is un-commenting the block below -- see CLAUDE.md, "Inert
            code kept on purpose", and read the note there first: the
            `complete_intro` gate has to come back in the same change or
            the questions are asked and never required.

          <QuestionOrderPicker
            pool={questions.data ?? []}
            picked={pickedQuestions}
            onChange={setPickedQuestions}
          />
        */}

        <div className="mt-4 grid grid-cols-1 gap-5">
          <div className="flex flex-col gap-4">
            {emptyCaseCount > 0 && (
              <p
                data-cy="empty-cases-hidden"
                className="sm:col-span-2 text-[11px] text-foreground-muted"
              >
                {emptyCaseCount} case{emptyCaseCount === 1 ? "" : "s"} with no
                images {emptyCaseCount === 1 ? "is" : "are"} not listed — upload
                a study to {emptyCaseCount === 1 ? "it" : "them"} in the Case
                library first.
              </p>
            )}
            {/* Practice first, then assessment — the order the candidate
                meets them in. Asking for the assessed cases first and the
                warm-up second reads backwards against the durations
                above, which are already in sitting order. */}
            {offersDryRun && (
              <CasePicker
                label={`Practice cases (${practiceCases.length})`}
                hint="Separate cases to warm up on. Not assessed."
                cases={sittableCases}
                picked={practiceCases}
                onToggle={(id) => setPracticeCases((v) => toggle(v, id))}
                cy="case-pick-practice"
              />
            )}
            <CasePicker
              label={`Assessment cases (${liveCases.length})`}
              hint="What the candidate is judged on."
              cases={sittableCases}
              picked={liveCases}
              onToggle={(id) => setLiveCases((v) => toggle(v, id))}
              cy="case-pick-live"
            />
          </div>
        </div>

        <button
          type="button"
          data-cy="btn-create-definition"
          disabled={!canCreate}
          onClick={() => {
            void create();
          }}
          className="sk-btn sk-btn-primary mt-5"
        >
          {editingId ? "Save changes" : "Create exam"}
        </button>
    </>
  );

  // Creating and editing are the SAME form and the same modal. They
  // differ only in whether an id is being written to, so keeping them
  // one dialog is what stops the two drifting apart.
  const formOpen = creating || editingId !== null;

  return (
    <div data-cy="test-builder" className="flex flex-col gap-8">
      <ExamListPanel
        candidates={candidates.data ?? []}
        onEdit={beginEdit}
        onCreate={() => {
          resetForm();
          setCreating(true);
        }}
      />

      {formOpen && (
        <Dialog.Root
          open
          onOpenChange={(next) => {
            if (!next) resetForm();
          }}
        >
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" />
            <Dialog.Content
              data-cy={editingId ? "edit-exam-modal" : "create-exam-modal"}
              className="fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-full max-w-3xl -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-xl border border-border bg-background-card shadow-theme-lg"
            >
              <div className="flex items-start justify-between gap-3 border-b border-border px-6 pt-6 pb-4">
                <div>
                  <Dialog.Title className="text-lg font-semibold text-foreground-heading">
                    {editingId ? "Edit exam" : "New exam"}
                  </Dialog.Title>
                  <Dialog.Description className="mt-1 text-xs text-foreground-muted">
                    {editingId
                      ? "Changes apply to candidates assigned from now on. Anyone who already has this exam keeps the version they were given."
                      : "Set the durations and pick the cases. Nothing is offered to anyone until you assign it."}
                  </Dialog.Description>
                </div>
                <Dialog.Close
                  data-cy="btn-close-exam-form"
                  aria-label="Close"
                  className="rounded-md p-1 text-foreground-muted hover:bg-table-row-hover hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                >
                  <X className="h-4 w-4" />
                </Dialog.Close>
              </div>
              <div className="px-6 py-5">{examForm}</div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      )}
    </div>
  );
}

/**
 * Pick the introduction questions AND say what order they come in.
 *
 * The order was always stored -- ``selected_question_ids`` is a list and
 * ``snapshot_questions`` walks it rather than the pool -- but nothing
 * could SET it. A checkbox list appends on tick, so the sequence a
 * candidate met was the sequence an admin happened to click in, which is
 * invisible on screen and impossible to correct without unticking
 * everything and starting again.
 *
 * So the chosen questions are their own list, numbered and movable, and
 * the pool below only offers what is not yet chosen. Two lists rather
 * than one is more chrome, but a single list cannot show both membership
 * and sequence without lying about one of them.
 */
export function QuestionOrderPicker({
  pool,
  picked,
  onChange,
}: {
  pool: { id: string; prompt: string; required?: boolean }[];
  picked: string[];
  onChange: (next: string[]) => void;
}) {
  const byId = new Map(pool.map((q) => [q.id, q]));
  // A retired question can still be selected on an existing exam, so
  // walk the PICKED ids and tolerate one the pool no longer offers
  // rather than silently dropping it from the exam on the next save.
  const chosen = picked.filter((id) => byId.has(id));
  const available = pool.filter((q) => !picked.includes(q.id));

  const move = (from: number, to: number) => {
    if (to < 0 || to >= chosen.length) return;
    const next = [...chosen];
    const [row] = next.splice(from, 1);
    next.splice(to, 0, row!);
    onChange(next);
  };

  return (
    <div>
      <h4 className="text-xs font-semibold uppercase tracking-wide text-foreground-muted">
        Introduction questions ({chosen.length})
      </h4>
      <p className="mt-1 text-[11px] text-foreground-muted">
        Asked in this order, before the cases. Pre-ticked from your last
        test.
      </p>

      <ol data-cy="question-order" className="mt-2 flex flex-col gap-1">
        {chosen.map((id, i) => (
          <li
            key={id}
            data-cy="question-chosen"
            className="flex items-start gap-2 rounded-md border border-border bg-background px-2 py-1.5 text-xs text-foreground"
          >
            <span className="w-4 shrink-0 pt-0.5 text-[11px] tabular-nums text-foreground-muted">
              {i + 1}
            </span>
            <span className="min-w-0 flex-1">{byId.get(id)?.prompt}</span>
            <span className="flex shrink-0 items-center gap-0.5">
              <button
                type="button"
                data-cy="btn-question-up"
                aria-label="Move up"
                title="Move up"
                disabled={i === 0}
                onClick={() => move(i, i - 1)}
                className="rounded p-1 text-foreground-muted hover:bg-table-row-hover hover:text-foreground disabled:opacity-30"
              >
                <ChevronUp className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                data-cy="btn-question-down"
                aria-label="Move down"
                title="Move down"
                disabled={i === chosen.length - 1}
                onClick={() => move(i, i + 1)}
                className="rounded p-1 text-foreground-muted hover:bg-table-row-hover hover:text-foreground disabled:opacity-30"
              >
                <ChevronDown className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                data-cy="btn-question-remove"
                aria-label="Remove"
                title="Remove from this exam"
                onClick={() => onChange(chosen.filter((x) => x !== id))}
                className="rounded p-1 text-foreground-muted hover:bg-table-row-hover hover:text-status-urgent"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          </li>
        ))}
        {chosen.length === 0 && (
          <li className="rounded-md border border-dashed border-border px-2 py-2 text-[11px] text-foreground-muted">
            None chosen — the candidate goes straight to the cases.
          </li>
        )}
      </ol>

      <p className="mt-3 text-[11px] font-semibold uppercase tracking-wide text-foreground-muted">
        Add from the pool
      </p>
      <ul className="mt-1 flex max-h-36 flex-col gap-0.5 overflow-auto">
        {available.map((q) => (
          <li key={q.id}>
            <button
              type="button"
              data-cy="question-pick"
              onClick={() => onChange([...chosen, q.id])}
              className="flex w-full items-start gap-2 rounded px-2 py-1 text-left text-xs text-foreground-muted hover:bg-table-row-hover hover:text-foreground"
            >
              <Plus className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span className="min-w-0">{q.prompt}</span>
            </button>
          </li>
        ))}
        {available.length === 0 && (
          <li className="px-2 text-[11px] text-foreground-muted">
            {pool.length === 0
              ? "No questions in the pool yet — add some under Questions."
              : "Every question in the pool is already on this exam."}
          </li>
        )}
      </ul>
    </div>
  );
}

function CasePicker({
  label,
  hint,
  cases,
  picked,
  onToggle,
  cy,
}: {
  label: string;
  hint: string;
  cases: { id: string; title: string; modality: string | null }[];
  picked: string[];
  onToggle: (id: string) => void;
  cy: string;
}) {
  return (
    <div>
      <h4 className="text-xs font-semibold uppercase tracking-wide text-foreground-muted">
        {label}
      </h4>
      <p className="mt-0.5 text-[11px] text-foreground-muted">{hint}</p>
      <ul className="mt-2 flex max-h-40 flex-col gap-1 overflow-auto">
        {cases.map((c) => (
          <li key={c.id}>
            <label className="flex items-start gap-2 text-xs text-foreground">
              <input
                type="checkbox"
                data-cy={cy}
                checked={picked.includes(c.id)}
                onChange={() => onToggle(c.id)}
                className="mt-0.5 h-4 w-4 accent-accent"
              />
              <span className="min-w-0">
                {c.title}
                {c.modality && (
                  <span className="ml-1 text-[11px] text-foreground-muted">
                    ({c.modality})
                  </span>
                )}
              </span>
            </label>
          </li>
        ))}
        {cases.length === 0 && (
          <li className="text-xs text-foreground-muted">
            No case has images yet — upload a study in the Case library.
          </li>
        )}
      </ul>
    </div>
  );
}
